import type { ReactNode } from "react";
import type { MessageDto, PlatformTypeName } from "@multi-chat/shared";
import { useTranslation } from "react-i18next";
import { ChannelIcon } from "../../../components/channel/ChannelIcon.js";
import {
  channelBadgeClassName,
  channelLabelI18nKey,
} from "../../../components/channel/channel-presentation.js";
import { LinkifiedText } from "../../../components/common/LinkifiedText/index.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { cn } from "../../../lib/utils.js";
import { WhatsAppTemplatePreview } from "../../whatsapp/WhatsAppTemplatePreview/index.js";
import { formatConversationClockTime, formatConversationFullDate } from "../conversation-time.js";
import { isOutboundMessage, messageBodyLabel } from "../inbox-display.js";
import { interactiveFallbackLabel } from "../interactive-fallback-label.js";
import { MessageAttachmentMedia } from "../MessageAttachmentMedia/index.js";
import { MessageContact } from "../MessageContact/index.js";
import { MessageLocation } from "../MessageLocation/index.js";
import { MessageUnsupported } from "../MessageUnsupported/index.js";
import {
  isStickerOnlyMessage,
  usesCaptionedMediaBubble,
  usesOverlayMediaBubble,
} from "./message-bubble-layout.js";
import { messageRowDataAttribute } from "../conversation-message-anchor.js";
import { MessageReplyPreview } from "../MessageReplyPreview/index.js";
import { MessageShareCard } from "../MessageShareCard/index.js";
import { MessageStoryReplyPreview } from "../MessageStoryReplyPreview/index.js";
import { MessageStatusIndicator } from "../MessageStatusIndicator/index.js";
import { useMessageActions } from "../MessageActions.js";
import { messageToolbarActions } from "../message-toolbar-actions.js";
import { MediaTimestampOverlay } from "../MediaTimestampOverlay/MediaTimestampOverlay.js";
import {
  presentationWithHeaderImageUrl,
  shouldResolveTemplateHeaderImage,
} from "../template-header-image.js";
import { useTemplateHeaderImageUrl } from "../use-template-header-image-url.js";
import { unsupportedMessageLabel } from "../unsupported-label.js";

function MessageBubbleMeta({
  message,
  time,
  outbound,
  platformType,
  className,
  onMedia = false,
}: {
  message: MessageDto;
  time: string | null;
  outbound: boolean;
  platformType: PlatformTypeName;
  className?: string;
  onMedia?: boolean;
}): React.JSX.Element {
  const { i18n } = useTranslation();
  const sentAt = message.platformSentAt ?? message.createdAt;
  return (
    <div
      className={cn(
        "flex shrink-0 items-center gap-1.5 text-[11px] leading-none tabular-nums",
        className,
      )}
    >
      {time ? (
        <time dateTime={sentAt} title={formatConversationFullDate(sentAt, i18n.language)}>
          {time}
        </time>
      ) : null}
      {outbound ? (
        <MessageStatusIndicator
          status={message.status}
          platformType={platformType}
          presentation={onMedia ? "onMedia" : "default"}
          failureCode={message.failureCode}
        />
      ) : null}
    </div>
  );
}

const BUBBLE_MAX_WIDTH_CLASS = "max-w-[min(32rem,66%)] max-md:max-w-[82%]";
const BUBBLE_TEXT_CLASS =
  "m-0 text-[0.84375rem] font-normal leading-[1.45] wrap-anywhere whitespace-pre-wrap text-inbox-strong [overflow-wrap:anywhere]";

/**
 * 14px corners. Inbound pinches the first bubble's top corner on the sender's side.
 * Outbound runs tighten the corners where bubbles meet; the last bubble's bottom-end
 * corner is square so the tail can grow out of it.
 */
function bubbleShapeClass({
  outbound,
  groupStart,
  groupEnd,
  tail,
}: {
  outbound: boolean;
  groupStart: boolean;
  groupEnd: boolean;
  tail: boolean;
}): string {
  if (!outbound) return groupStart ? "rounded-[14px] rounded-ss-[5px]" : "rounded-[14px]";
  return cn(
    "rounded-[14px]",
    !groupStart && "rounded-se-[6px]",
    groupEnd ? (tail ? "rounded-ee-none" : null) : "rounded-ee-[6px]",
  );
}

/** Tail on the last outgoing bubble of a run, pointing at the channel disc. */
function OutboundBubbleTail(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 8 13"
      className="pointer-events-none absolute -end-[7px] bottom-0 h-[13px] w-2 overflow-visible rtl:-scale-x-100"
      aria-hidden="true"
    >
      <path d="M0 0Q0.6 9.4 8 13H0z" className="fill-message-outbound" />
      <path
        d="M0.5 0Q1.1 9 7.6 12.5H0"
        className="fill-none stroke-message-outbound-border"
        strokeWidth={1}
      />
    </svg>
  );
}

/** Keeps the bubble's own width cap while letting the tail sit outside its clipped edge. */
function BubbleTailFrame({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <div className={cn("relative flex min-w-0 flex-col items-end", BUBBLE_MAX_WIDTH_CLASS)}>
      {children}
      <OutboundBubbleTail />
    </div>
  );
}

/** Brand disc beside the last outgoing message of a run naming the channel and account. */
function MessageChannelDisc({
  platformType,
  accountName,
}: {
  platformType: PlatformTypeName;
  accountName?: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const channel = t(`channel.${channelLabelI18nKey(platformType)}`);
  const label = accountName
    ? t("inbox.message.sentViaAccount", { channel, account: accountName })
    : t("inbox.message.sentVia", { channel });
  return (
    <SimpleTooltip content={label}>
      <span
        role="img"
        aria-label={label}
        className={cn(
          "mb-0.5 inline-grid size-[18px] shrink-0 place-items-center rounded-full ring-[1.5px] ring-white",
          channelBadgeClassName(platformType),
        )}
      >
        <ChannelIcon platformType={platformType} size={10} className="text-white" />
      </span>
    </SimpleTooltip>
  );
}

function MessageRow({
  message,
  outbound,
  platformType,
  accountName,
  groupEnd,
  turnGap,
  className,
  toolbar,
  gestures,
  children,
}: {
  message: MessageDto;
  outbound: boolean;
  platformType: PlatformTypeName;
  accountName?: string | null;
  groupEnd: boolean;
  turnGap?: boolean;
  className?: string;
  toolbar?: React.ReactNode;
  gestures?: React.HTMLAttributes<HTMLLIElement>;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <li
      {...messageRowDataAttribute(message.messageId)}
      {...gestures}
      className={cn(
        "group/bubble flex items-end",
        outbound ? "justify-end gap-3" : "justify-start gap-1.5",
        turnGap && "mt-2.5",
        className,
      )}
    >
      {outbound ? toolbar : null}
      {children}
      {outbound ? null : toolbar}
      {outbound ? (
        groupEnd ? (
          <MessageChannelDisc platformType={platformType} accountName={accountName} />
        ) : (
          <span className="size-[18px] shrink-0" aria-hidden="true" />
        )
      ) : null}
    </li>
  );
}

export function MessageBubble({
  message,
  locale,
  platformType,
  organizationId,
  accountName,
  turnGap,
  groupStart = false,
  groupEnd = true,
  closed = false,
  onReply,
  onJumpToQuotedMessage,
  className,
}: {
  message: MessageDto;
  locale: string;
  platformType: PlatformTypeName;
  organizationId: number;
  /** Connected account the conversation's endpoint belongs to, for the channel disc tooltip. */
  accountName?: string | null;
  turnGap?: boolean;
  /** First message of a visual run (see `continuesMessageRun`). */
  groupStart?: boolean;
  /** Last message of a visual run; only it shows the outbound channel disc and tail. */
  groupEnd?: boolean;
  /** Hides Reply while the conversation is closed. Copy stays available. */
  closed?: boolean;
  onReply?: (message: MessageDto) => void;
  onJumpToQuotedMessage?: (messageId: string) => void;
  className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const outbound = isOutboundMessage(message);
  const time = formatConversationClockTime(message.platformSentAt ?? message.createdAt, locale);
  const storedPresentation = message.templatePresentation;
  const headerImageUrl = useTemplateHeaderImageUrl({
    organizationId,
    conversationId: message.conversationId,
    messageId: message.messageId,
    enabled: shouldResolveTemplateHeaderImage(storedPresentation),
  });
  const presentation = storedPresentation
    ? presentationWithHeaderImageUrl(storedPresentation, headerImageUrl)
    : storedPresentation;
  const isInteractive = message.messageType === "INTERACTIVE" && !presentation;
  const mediaAttachments = message.attachments ?? [];
  const toolbarActions = messageToolbarActions({
    platformType,
    content: message.content,
    closed,
  });
  const messageActions = useMessageActions({
    reply: toolbarActions.reply && onReply != null,
    copyText: toolbarActions.copyText,
    onReply: onReply ? () => onReply(message) : undefined,
  });
  const overlayMedia = usesOverlayMediaBubble(message);
  const captionedMedia = usesCaptionedMediaBubble(message);
  const stickerOnly = isStickerOnlyMessage(message);
  const imageBubbleLayout = overlayMedia || captionedMedia ? "bubble" : "default";

  const bubbleChrome = outbound
    ? "border-message-outbound-border bg-message-outbound"
    : "border-transparent bg-message-inbound";
  const tail = outbound && groupEnd && !overlayMedia && !stickerOnly;
  const bubbleShape = bubbleShapeClass({ outbound, groupStart, groupEnd, tail });
  const rowProps = {
    message,
    outbound,
    platformType,
    accountName,
    groupEnd,
    turnGap,
    className,
    toolbar: messageActions.toolbar,
    gestures: messageActions.gestures,
  };

  const replyBlock = message.replyContext ? (
    <MessageReplyPreview
      replyContext={message.replyContext}
      organizationId={organizationId}
      onJumpToQuotedMessage={onJumpToQuotedMessage}
    />
  ) : null;

  const storyReplyBlock = message.storyReplyPresentation ? (
    <MessageStoryReplyPreview
      presentation={message.storyReplyPresentation}
      organizationId={organizationId}
    />
  ) : null;

  const quoteContextBlock =
    replyBlock || storyReplyBlock ? (
      <>
        {replyBlock}
        {storyReplyBlock}
      </>
    ) : null;

  const mediaBlock = mediaAttachments.map((attachment) => (
    <MessageAttachmentMedia
      key={attachment.attachmentId}
      organizationId={organizationId}
      attachment={attachment}
      locale={locale}
      layout={imageBubbleLayout}
    />
  ));

  if (overlayMedia) {
    return (
      <MessageRow {...rowProps}>
        <div className={cn(
          "inline-flex flex-col",
          BUBBLE_MAX_WIDTH_CLASS,
          outbound ? "items-end" : "items-start",
        )}>
          {quoteContextBlock ? (
            <div
              className={cn(
                "mb-1 w-fit max-w-full border px-[11px] py-1",
                bubbleChrome,
                bubbleShape,
                outbound ? "self-end" : "self-start",
              )}
            >
              {quoteContextBlock}
            </div>
          ) : null}
          <div
            className={cn(
              "relative w-fit max-w-full overflow-hidden ring-1 ring-inset",
              bubbleShape,
              outbound ? "ring-message-outbound-border" : "ring-inbox-line-soft",
            )}
          >
            {mediaBlock}
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-14 bg-gradient-to-t from-ink/70 via-ink/35 to-transparent"
              aria-hidden="true"
            />
            <MediaTimestampOverlay>
              <MessageBubbleMeta
                message={message}
                time={time}
                outbound={outbound}
                platformType={platformType}
                onMedia
                className="text-surface-elevated/95"
              />
            </MediaTimestampOverlay>
          </div>
        </div>
      </MessageRow>
    );
  }

  if (captionedMedia) {
    const captionedBubble = (
      <div
        className={cn(
          "w-fit overflow-hidden border",
          tail ? "max-w-full" : BUBBLE_MAX_WIDTH_CLASS,
          bubbleChrome,
          bubbleShape,
        )}
      >
        {quoteContextBlock ? <div className="px-[11px] pt-1.5">{quoteContextBlock}</div> : null}
        <div className="w-fit max-w-full overflow-hidden">{mediaBlock}</div>
        <div className="px-[11px] pt-1 pb-[5px]">
          <p dir="auto" className={BUBBLE_TEXT_CLASS}>
            <LinkifiedText text={message.content ?? ""} />
          </p>
          <div className="mt-0.5 flex justify-end">
            <MessageBubbleMeta
              message={message}
              time={time}
              outbound={outbound}
              platformType={platformType}
              className="text-inbox-subtle"
            />
          </div>
        </div>
      </div>
    );
    return (
      <MessageRow {...rowProps}>
        {tail ? <BubbleTailFrame>{captionedBubble}</BubbleTailFrame> : captionedBubble}
      </MessageRow>
    );
  }

  if (stickerOnly) {
    return (
      <MessageRow {...rowProps}>
        <div
          className={cn(
            "inline-flex flex-col bg-transparent",
            BUBBLE_MAX_WIDTH_CLASS,
            outbound ? "items-end" : "items-start",
          )}
        >
          {quoteContextBlock ? (
            <div
              className={cn(
                "mb-1 w-fit max-w-full border px-[11px] py-1",
                bubbleChrome,
                bubbleShape,
              )}
            >
              {quoteContextBlock}
            </div>
          ) : null}
          <div className="relative bg-transparent">{mediaBlock}</div>
          <MessageBubbleMeta
            message={message}
            time={time}
            outbound={outbound}
            platformType={platformType}
            className="mt-0.5 text-inbox-subtle"
          />
        </div>
      </MessageRow>
    );
  }

  const structuredBody = message.locationPresentation ? (
    <MessageLocation presentation={message.locationPresentation} />
  ) : message.contactPresentation ? (
    <MessageContact
      organizationId={organizationId}
      presentation={message.contactPresentation}
    />
  ) : message.sharePresentation ? (
    <MessageShareCard
      organizationId={organizationId}
      presentation={message.sharePresentation}
    />
  ) : message.unsupportedPresentation ? (
    <MessageUnsupported
      label={unsupportedMessageLabel(
        message.unsupportedPresentation.kind,
        t,
        message.unsupportedPresentation,
      )}
    />
  ) : isInteractive && !message.content?.trim() ? (
    <MessageUnsupported label={interactiveFallbackLabel(message.interactivePresentation, t)} />
  ) : null;

  const bubble = (
    <div
      className={cn(
        "w-fit min-w-0 overflow-hidden border px-[11px] pt-1.5 pb-[5px]",
        tail ? "max-w-full" : BUBBLE_MAX_WIDTH_CLASS,
        bubbleChrome,
        bubbleShape,
      )}
    >
      {quoteContextBlock}
      {mediaBlock}
      {structuredBody}

      <div className="flex flex-wrap items-end justify-end gap-x-1.5">
        <div className="min-w-0 grow basis-[7.5rem]">
          {presentation ? (
            <WhatsAppTemplatePreview presentation={presentation} compact />
          ) : structuredBody ? null : message.content || mediaAttachments.length === 0 ? (
            <div className="grid gap-0.5">
              {isInteractive && message.content?.trim() ? (
                <p className="m-0 text-[0.625rem] font-medium tracking-[0.04em] text-inbox-subtle uppercase">
                  {t("inbox.messageType.INTERACTIVE")}
                </p>
              ) : null}
              <p dir="auto" className={BUBBLE_TEXT_CLASS}>
                <LinkifiedText text={messageBodyLabel(message, t)} />
              </p>
            </div>
          ) : null}
        </div>

        <MessageBubbleMeta
          message={message}
          time={time}
          outbound={outbound}
          platformType={platformType}
          className="mb-px text-inbox-subtle"
        />
      </div>

    </div>
  );
  return (
    <MessageRow {...rowProps}>
      {tail ? <BubbleTailFrame>{bubble}</BubbleTailFrame> : bubble}
    </MessageRow>
  );
}
